import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  TextField,
  MenuItem,
  useTheme,
} from '@mui/material';
import PageHeader from '../components/PageHeader';
import './ProjectBoard.css';

export default function ProjectBoardView({ project, entries, onFieldChange }) {
  const theme = useTheme();

  function normalizeYes(v) {
    if (v === true) return true;
    if (v === false) return false;
    if (v === 1) return true;
    if (v === 0) return false;
    if (v == null) return false;

    const s = String(v).trim().toLowerCase();
    if (s === 'yes' || s === 'true' || s === '1') return true;
    if (s === 'no' || s === 'false' || s === '0' || s === '') return false;
    return false;
  }

  function toTriState(valueBool, queriesBool) {
    if (normalizeYes(queriesBool)) return 'Query';
    return normalizeYes(valueBool) ? 'Yes' : 'No';
  }

  function triStateToFields(next) {
    if (next === 'Query') return { value_bool: false, queries_bool: true };
    return { value_bool: next === 'Yes', queries_bool: false };
  }

  const computed = useMemo(() => {
    return (entries || []).map((raw) => {
      const kind = raw.kind || raw.type; // "HEADER" | "INFO" | "STEP"
      const row = raw.row_idx ?? raw.row ?? 1;
      const col = raw.col_idx ?? raw.col ?? 1;

      const hasQ = (raw.has_queries ?? raw.hasQueries) ? true : false;

      const labelCol = 2 * col - 1;
      const inputCol = 2 * col;

      return {
        _key: raw.id ?? `${raw.name}-${row}-${col}`,
        kind,
        name: raw.name,
        valueText: raw.value_text ?? raw.value ?? '',
        valueBool:
          typeof raw.value_bool === 'boolean'
            ? raw.value_bool
            : raw.value === 'Yes',
        hasQ,
        queriesBool:
          typeof raw.queries_bool === 'boolean'
            ? raw.queries_bool
            : raw.queries === 'Yes',
        row,
        labelCol,
        inputCol,
      };
    });
  }, [entries]);

  const maxCol = useMemo(() => {
    let m = 2;
    for (const e of computed) m = Math.max(m, e.inputCol || 2);
    return m;
  }, [computed]);

  // Shared, consistent MUI shape (not oval)
  const inputRadius = 1.5; // MUI spacing unit-based radius (looks “enterprise”, not pill)

  return (
    <Box
      className="project-board-view"
      sx={{
        width: '100%',
        // Force theme background (kills "random white" if CSS is leaking)
        bgcolor: 'background.default',
        color: 'text.primary',
        p: 0,
      }}
    >
      {project && (
        <Box sx={{ mb: 2 }}>
          <PageHeader
            title={project.client_name}
            subtitle={`${project.loan_type ? `(${project.loan_type}) ` : ''}${
              project.created_at
                ? `• Created: ${new Date(project.created_at).toLocaleDateString()}`
                : ''
            }`}
            actions={
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={(project.status || '—').toString()}
                  size="small"
                  variant="outlined"
                  color={
                    (project.status || '').toLowerCase() === 'active'
                      ? 'success'
                      : 'default'
                  }
                />
              </Stack>
            }
          />
        </Box>
      )}

      {!computed || computed.length === 0 ? (
        <Box sx={{ opacity: 0.75 }}>No fields found.</Box>
      ) : (
        <Paper
          elevation={0}
          // Force MUI surface (no white)
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gap: '10px 16px',
              alignItems: 'center',
              gridTemplateColumns: `repeat(${maxCol}, 1fr)`,
            }}
          >
            {computed.map((e) => {
              if (e.kind === 'HEADER') {
                return (
                  <Box
                    key={e._key}
                    sx={{
                      gridColumn: '1 / -1',
                      gridRow: e.row,
                      fontWeight: 700,
                      fontSize: 14,
                      letterSpacing: 0.2,
                      color: 'text.primary',
                      opacity: 0.9,
                      pt: 1,
                      pb: 0.5,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {e.name}
                  </Box>
                );
              }

              if (e.kind === 'INFO') {
                return (
                  <React.Fragment key={e._key}>
                    <Box
                      component="label"
                      sx={{
                        gridRow: e.row,
                        gridColumn: e.labelCol,
                        color: 'text.secondary',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {e.name}
                    </Box>

                    <TextField
                      size="small"
                      variant="outlined"
                      sx={{
                        gridRow: e.row,
                        gridColumn: e.inputCol,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: inputRadius,
                          bgcolor: 'background.paper',
                        },
                      }}
                      value={e.valueText || ''}
                      onChange={(ev) =>
                        onFieldChange?.(e._key, 'value_text', ev.target.value)
                      }
                    />
                  </React.Fragment>
                );
              }

              if (e.kind === 'STEP') {
                const tri = e.hasQ
                  ? toTriState(e.valueBool, e.queriesBool)
                  : e.valueBool
                  ? 'Yes'
                  : 'No';

                return (
                  <React.Fragment key={e._key}>
                    <Box
                      component="label"
                      sx={{
                        gridRow: e.row,
                        gridColumn: e.labelCol,
                        color: 'text.secondary',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {e.name}
                    </Box>

                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      sx={{
                        gridRow: e.row,
                        gridColumn: e.inputCol,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: inputRadius, // 👈 kills “oval”
                          bgcolor: 'background.paper',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'divider',
                        },
                      }}
                      value={tri}
                      onChange={(ev) => {
                        const next = ev.target.value;

                        if (e.hasQ) {
                          const patch = triStateToFields(next);
                          onFieldChange?.(e._key, 'value_bool', patch.value_bool);
                          onFieldChange?.(
                            e._key,
                            'queries_bool',
                            patch.queries_bool
                          );
                        } else {
                          onFieldChange?.(e._key, 'value_bool', next === 'Yes');
                        }
                      }}
                    >
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                      {e.hasQ ? <MenuItem value="Query">Query</MenuItem> : null}
                    </TextField>
                  </React.Fragment>
                );
              }

              return null;
            })}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
